import { Box, Divider, Paper, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import {
  formatModifiedOn,
  missingValue,
  skuStatusDotColor,
} from "pages/SkusPage/SkuSummarySidebar/skuPublishCardUtils";

const styles = {
  header: {
    px: 2,
    py: 1.5,
    bgcolor: "grey.100",
    borderBottom: 1,
    borderColor: "divider",
  },
  title: {
    color: "text.primary",
    fontWeight: 500,
  },
  body: {
    p: 2,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  status: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    fontWeight: 500,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
  stamps: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    columnGap: 2,
    rowGap: 1,
    m: 0,
  },
  stampValue: {
    m: 0,
    textAlign: "right",
    overflowWrap: "anywhere",
  },
};

type SkuPublishCardProps = {
  skuStatusId?: string;
  skuStatusName?: string;
  modifiedOn?: Date;
  modifiedBy?: string;
  /**
   * Edit mode: the SKU Details form renders its status field and its Cancel/Update buttons into these
   * elements, so they stay bound to that form while appearing in the sidebar.
   */
  editSlots?: {
    status: (element: HTMLDivElement | null) => void;
    actions: (element: HTMLDivElement | null) => void;
  };
};

const SkuPublishCard = ({
  skuStatusId,
  skuStatusName,
  modifiedOn,
  modifiedBy,
  editSlots,
}: SkuPublishCardProps) => {
  const { formatMessage: t } = useIntl();

  return (
    <Paper variant="outlined">
      <Box sx={styles.header}>
        <Typography variant="subtitle1" component="h2" sx={styles.title}>
          {t({ id: "publish" })}
        </Typography>
      </Box>

      <Box sx={styles.body}>
        {editSlots ? (
          <Box key="statusSlot" ref={editSlots.status} />
        ) : (
          <Box key="statusLabel">
            <Typography variant="caption" color="text.secondary">
              {t({ id: "skuFormSkuStatusLabel" })}
            </Typography>
            <Box sx={styles.status}>
              <Box
                aria-hidden
                sx={[styles.dot, { bgcolor: skuStatusDotColor(skuStatusId) }]}
              />
              {skuStatusName || missingValue}
            </Box>
          </Box>
        )}

        <Divider />

        <Box component="dl" sx={styles.stamps}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {t({ id: "lastModified" })}
          </Typography>
          <Typography component="dd" variant="body2" sx={styles.stampValue}>
            {formatModifiedOn(modifiedOn)}
          </Typography>
          <Typography component="dt" variant="body2" color="text.secondary">
            {t({ id: "modifiedBy" })}
          </Typography>
          <Typography component="dd" variant="body2" sx={styles.stampValue}>
            {modifiedBy || missingValue}
          </Typography>
        </Box>

        {editSlots && (
          <>
            <Divider />
            <Box ref={editSlots.actions} />
          </>
        )}
      </Box>
    </Paper>
  );
};

export default SkuPublishCard;
